import React, {useCallback, useEffect} from 'react'
import ListItemText from '@mui/material/ListItemText'
import Slider from '@mui/material/Slider'
import ListItem from '@mui/material/ListItem'
import styled from 'styled-components'
import Grid from '@mui/material/GridLegacy'
import StyledTextField from '@/components/pages/NewDatasetJobPage/StyledTextField'
import {ThemeColorsHospitalic} from '@/assets/styles/color/ThemeColorsHospitalic'
import StyledCheckbox from '@/components/pages/NewDatasetJobPage/StyledCheckbox'
import {NumFilterOptions} from '@/components/pages/NewDatasetJobPage/types'
import FractionNonMissingTooltip from '@/components/common/FractionNonMissingTooltip'

const replaceDecimal = (value: string) => {
    return value.replace(',', '.')
}

const StyledSlider = styled(Slider)`
    & label {
        color: ${ThemeColorsHospitalic.TEXT_PRIMARY};
    }
`
type NumberFilterItemProps = {
    numberFilterOpts: NumFilterOptions
    checked: boolean
    inputMin?: number | string
    inputMax?: number | string
    onFilterChange: (filterName: string, checked: boolean, filterVal: any) => void
}

const NumberFilterItem = ({numberFilterOpts, inputMin, inputMax, checked, onFilterChange}: NumberFilterItemProps) => {
    const [sliderValue, setSliderValue] = React.useState<number[]>([numberFilterOpts?.min_value || 0, numberFilterOpts?.max_value || 0])

    const onMinChange = useCallback((evt: any) => {
        const value = replaceDecimal(evt.target.value)

        const isEmptyString = value === ''
        const numberValue = value.endsWith('.') ? NaN : Number(value)

        onFilterChange(numberFilterOpts.name, true, {
            min: isNaN(numberValue) || isEmptyString ? value : numberValue,
            max: inputMax || numberFilterOpts.freeText ? inputMax : numberFilterOpts.max_value
        })
    }, [numberFilterOpts, inputMax])

    const validateMinAndChange = useCallback((evt: any) => {
        if (numberFilterOpts.freeText) {
            return
        }

        let val = Number(evt.target.value)

        if (isNaN(val) || val < numberFilterOpts.min_value || val > numberFilterOpts.max_value || (inputMax !== undefined && val > inputMax)) {
            val = numberFilterOpts.min_value
        }
        onFilterChange(numberFilterOpts.name, true, {min: val, max: inputMax ? inputMax : numberFilterOpts.max_value})
        setSliderValue(prev => {
            const sliderVals = [...prev]
            sliderVals[0] = val
            return sliderVals
        })
    }, [numberFilterOpts, inputMax])

    const onMaxChange = useCallback((evt: any) => {
        const value = replaceDecimal(evt.target.value)
        const isEmptyString = value === ''
        const numberValue = value.endsWith('.') ? NaN : Number(value)

        onFilterChange(numberFilterOpts.name, true, {
            min: inputMin || numberFilterOpts.freeText ? inputMin : numberFilterOpts.min_value,
            max: isNaN(numberValue) || isEmptyString ? value : numberValue
        })
    }, [numberFilterOpts, inputMin])

    const validateMaxAndChange = useCallback((evt: any) => {
        if (numberFilterOpts.freeText) {
            return
        }

        let val = Number(evt.target.value)

        if (isNaN(val) || val < numberFilterOpts.min_value || val > numberFilterOpts.max_value || (inputMin !== undefined && val < inputMin)) {
            val = numberFilterOpts.max_value
        }
        onFilterChange(numberFilterOpts.name, true, {min: inputMin ? inputMin : numberFilterOpts.min_value, max: val})
        setSliderValue(prev => {
            const sliderVals = [...prev]
            sliderVals[1] = val
            return sliderVals
        })
    }, [numberFilterOpts, inputMin])

    const onCheck = useCallback(() => {
        if (checked) {
            onFilterChange(numberFilterOpts.name, false, undefined)
            setSliderValue([numberFilterOpts.min_value, numberFilterOpts.max_value])
        } else {
            onFilterChange(numberFilterOpts.name, true, {
                min: numberFilterOpts.freeText ? inputMin : numberFilterOpts.min_value,
                max: numberFilterOpts.freeText ? inputMax : numberFilterOpts.max_value
            })
        }
    }, [checked, numberFilterOpts, inputMin, inputMax])

    const handleSliderChange = useCallback((event: Event, newValue: number | number[]) => {
        setSliderValue(newValue as number[])
    }, [])

    const handleSliderCommit = useCallback(() => {
        onFilterChange(numberFilterOpts.name, true, {min: sliderValue[0], max: sliderValue[1]})
    }, [numberFilterOpts, sliderValue])

    useEffect(() => {
        // reset values on database select change
        if (!inputMin && !inputMax) {
            setSliderValue([numberFilterOpts.min_value, numberFilterOpts.max_value])
        }
    }, [inputMin, inputMax])

    return (
        <ListItem disablePadding>
            <Grid container spacing={2} paddingRight={1}>
                <Grid item xs={6} display="flex" alignItems="center">
                    <StyledCheckbox checked={checked} onClick={onCheck}/>
                    <FractionNonMissingTooltip fraction={numberFilterOpts.fraction_non_missing}>
                        <ListItemText className="align-middle"
                            primary={numberFilterOpts.name}/>
                    </FractionNonMissingTooltip>
                </Grid>
                <Grid item xs={3}>
                    <StyledTextField
                        label="Min value"
                        size="small"
                        value={(inputMin !== null && inputMin !== undefined) ? inputMin : (numberFilterOpts.freeText ? '' : numberFilterOpts.min_value)}
                        onChange={onMinChange}
                        onBlur={validateMinAndChange}
                        onKeyDown={(evt: any) => {
                            if (evt.key === 'Enter') {
                                validateMinAndChange(evt)
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={3}>
                    <StyledTextField
                        label="Max value"
                        size="small"
                        value={(inputMax !== null && inputMax !== undefined) ? inputMax : (numberFilterOpts.freeText ? '' : numberFilterOpts.max_value)}
                        onChange={onMaxChange}
                        onBlur={validateMaxAndChange}
                        onKeyDown={(evt: any) => {
                            if (evt.key === 'Enter') {
                                validateMaxAndChange(evt)
                            }
                        }}
                        slotProps={{
                            input: {
                                inputMode: 'decimal'
                            },
                        }}
                    />
                </Grid>
                <Grid item xs={12} marginX={2}>
                    {!numberFilterOpts.freeText && (
                        <StyledSlider
                            min={numberFilterOpts.min_value}
                            max={numberFilterOpts.max_value}
                            valueLabelDisplay="auto"
                            value={sliderValue}
                            onChange={handleSliderChange}
                            onChangeCommitted={handleSliderCommit}
                            step={Math.abs(numberFilterOpts.min_value - numberFilterOpts.max_value) <= 1 ? 0.01 : 1}
                        />
                    )}
                </Grid>
            </Grid>
        </ListItem>
    )
}

export default NumberFilterItem